import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { NumericFormat } from "react-number-format";
import { useNavigate } from "react-router-dom";
import { useCreateMutation } from "../../../../features/usersApi";
import { addBannerWithTimeout } from "../../../lib/centralizedBanner/slice";
import { useAppDispatch, useAppSelector } from "../../../lib/redux/hooks";
import { Layout } from "../../../ui/layout";
import Avatar from "../../../ui/molecules/avatar";
import Button from "../../../ui/molecules/button";
import Input from "../../../ui/molecules/input";
import { SelectCustom } from "../../../ui/molecules/select";
import { Spinner } from "../../../ui/molecules/spinner";
import Toggle from "../../../ui/molecules/toggle";
import FileUploader from "../../../ui/organisms/fileUploader";
import { MenuItems } from "../../../ui/organisms/navbar/dto";
import { CustomerAccessEnum, UserRole, UserRoleMap } from "../dto";
import { SelectUser } from "../select/userSelect";
import { setCreateFromProject } from "../slice";
import { UserFormValues, validateNewUserSchema } from "./validation";

export function NewUser() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const atecos = useAppSelector((s) => s.atecos.findAllAtecosResponse ?? []);
  const createFromProject = useAppSelector((s) => s.users.createFromProject);
  const [createUser, { isLoading, reset }] = useCreateMutation();
  const [file, setFile] = useState<File | null>(null);

  const {
    control,
    handleSubmit,
    watch,
    setError,
    formState: { errors },
  } = useForm<UserFormValues>({
    resolver: zodResolver(validateNewUserSchema),
    defaultValues: {
      role: createFromProject?.userRole ?? undefined,
      name: "",
      surname: "",
      email: "",
      phone: "",
      companyRevenue: undefined,
      companyName: undefined,
      companyVat: undefined,
      companyAtecoCode: undefined,
      companySector: undefined,
      collaborators: undefined,
      customerId: undefined,
      customerAccess: CustomerAccessEnum.NONE,
      isAutonomous: false,
    } as any,
  });

  const role = watch("role");
  const companyAtecoCode = watch("companyAtecoCode");
  const companySector = companyAtecoCode
    ? atecos.find((a: any) => a.atecoCode === companyAtecoCode)?.sector
    : undefined;

  const preview = file ? URL.createObjectURL(file) : "";

  const handleSuccess = (newUserId: string) => {
    if (!createFromProject) {
      dispatch(addBannerWithTimeout({
        message: "Utente creato con successo",
        variant: "success"
      }));
      navigate("/users");
    } else {
      dispatch(setCreateFromProject({ userId: newUserId, userRole: createFromProject.userRole }));
      navigate("/new-project");
    }
  };

  const onSubmit = async (data: UserFormValues) => {
    reset();

    try {
      const formData = new FormData();
      formData.append("user", JSON.stringify(data));
      if (file) {
        formData.append("file", file);
      }

      const newUserId = await createUser(formData).unwrap();
      handleSuccess(newUserId.id);
    } catch (err: any) {
      const errors = err?.data?.errors ?? [];
      if (Array.isArray(errors) && errors.length > 0) {
        errors.forEach((e: { message: string }) => {
          const msg = e.message.toLowerCase();
          if (msg.includes("already exists"))
            setError("email", { message: "Email già esistente" });
          else if (msg.includes("sending action email to user"))
            dispatch(addBannerWithTimeout({
              message: "Si è verificato un errore nell'invio dell'email",
              variant: "error"
            }));
        });
      } else {
        dispatch(addBannerWithTimeout({
          message: "Si è verificato un errore durante la creazione dell'utente",
          variant: "error"
        }));
      }
    }
  };

  const toValue = (v: any) => {
    if (v == null) return undefined;
    if (Array.isArray(v)) return v.map((it) => (it?.value ?? it));
    return v.value ?? v;
  };

  return (
    <Layout
      menuItem={MenuItems.USERS}
      breadcrumbItems={["Utenti", "Nuovo utente"]}
      headerLabel="Nuovo utente"
      headerBackPath={createFromProject ? "/new-project" : "/users"}
      headerChildren={
        <div className="flex items-center h-full w-full justify-end">
          {isLoading ? (
            <Spinner />
          ) : (
            <Button size={"sm"} iconPosition={"off"} label="Salva utente" variant={"solid"} color={"blue"} onClick={handleSubmit(onSubmit)} />
          )}
        </div>
      }
    >
      <div className="gap-[24px] h-full max-h-full flex flex-col w-[100%] p-[16px]">
        <div className="p-[24px] gap-[20px] h-full max-h-full overflow-auto flex flex-col bg-white shadow-sm rounded-[12px]">
          {/* RUOLO */}
          <div className="flex gap-[24px] w-[100%] items-start">
            <div className="w-[240px] text-label-md font-medium text-neutral-600">Ruolo <span className="text-red-600"> *</span></div>
            <div className="flex gap-[16px] w-[100%]">
              <Controller
                control={control}
                name="role"
                render={({ field }) => (
                  <SelectCustom
                    isClearable
                    disabled={!!createFromProject}
                    defaultValue={field.value}
                    onChange={(v) => field.onChange(toValue(v))}
                    error={!!errors.role}
                    errorLabel={String(errors.role?.message ?? "")}
                    placeholder={"Seleziona un ruolo"}
                    options={[
                      { label: UserRoleMap.get(UserRole.collaborator)!, value: UserRole.collaborator },
                      { label: UserRoleMap.get(UserRole.commercial)!, value: UserRole.commercial },
                      { label: UserRoleMap.get(UserRole.customer)!, value: UserRole.customer },
                      { label: UserRoleMap.get(UserRole.team_leader)!, value: UserRole.team_leader },
                      { label: UserRoleMap.get(UserRole.admin)!, value: UserRole.admin },
                    ]}
                  />
                )}
              />
              {/* isAutonomous */}
              {role === UserRole.commercial && (
                <div className="min-w-[150px] flex">
                  <Controller
                    name="isAutonomous"
                    control={control}
                    render={({ field }) => (
                      <Toggle placeholder="È autonomo" checked={field.value} onChange={(e) => field.onChange(e)} />
                    )}
                  />
                </div>
              )}
              {/* customer toggles */}
              {role === UserRole.customer && (
                <>
                  <div className="min-w-[280px] flex">
                    <Controller
                      name="customerAccess"
                      control={control}
                      render={({ field }) => (
                        <Toggle
                          placeholder="Può accedere alle liste target"
                          checked={field.value !== CustomerAccessEnum.NONE}
                          onChange={(e) => field.onChange(e ? CustomerAccessEnum.PARTIAL : CustomerAccessEnum.NONE)}
                        />
                      )}
                    />
                  </div>
                  <div className="min-w-[320px] flex">
                    <Controller
                      name="customerAccess"
                      control={control}
                      render={({ field }) => (
                        <Toggle
                          placeholder="Ha accesso completo ai contatti"
                          disabled={field.value === CustomerAccessEnum.NONE}
                          checked={field.value === CustomerAccessEnum.FULL}
                          onChange={(e) => field.onChange(e ? CustomerAccessEnum.FULL : CustomerAccessEnum.PARTIAL)}
                        />
                      )}
                    />
                  </div>
                </>
              )}
            </div>
          </div>

          {/* EMAIL */}
          <div className="flex gap-[24px] w-[100%] items-start">
            <div className="w-[240px] text-label-md font-medium text-neutral-600">Email <span className="text-red-600"> *</span></div>
            <div className="flex gap-[16px] w-[100%]">
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    value={field.value || ""}
                    placeholder="Email"
                    supportingText={errors.email?.message as string | undefined}
                    error={!!errors.email}
                  />
                )}
              />
            </div>
          </div>

          {/* NOME / COGNOME */}
          <div className="flex gap-[24px] w-[100%] items-start">
            <div className="w-[240px] text-label-md font-medium text-neutral-600">Nome <span className="text-red-600"> *</span></div>
            <div className="flex gap-[16px] w-[100%]">
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <Input {...field} value={field.value || ""} placeholder="Nome" supportingText={errors.name?.message as string | undefined} error={!!errors.name} />
                )}
              />
              <Controller
                name="surname"
                control={control}
                render={({ field }) => (
                  <Input {...field} value={field.value || ""} placeholder="Cognome" supportingText={errors.surname?.message as string | undefined} error={!!errors.surname} />
                )}
              />
            </div>
          </div>

          {/* PHONE */}
          <div className="flex gap-[24px] w-[100%] items-start">
            <div className="w-[240px] text-label-md font-medium text-neutral-600">Smartphone <span className="text-red-600"> *</span></div>
            <div className="flex gap-[16px] w-[100%]">
              <Controller
                name="phone"
                control={control}
                render={({ field }) => (
                  <Input {...field} value={field.value || ""} placeholder="Smartphone" supportingText={errors.phone?.message as string | undefined} error={!!errors.phone} />
                )}
              />
            </div>
          </div>
          {role === UserRole.customer && (
            <>
              {/* Collaboratori */}
              <div className="flex gap-[24px] w-[100%] items-start">
                <div className="w-[240px] text-label-md font-medium text-neutral-600">Collaboratori</div>
                <div className="flex gap-[16px] w-[100%]">
                  <Controller
                    name="collaborators"
                    control={control}
                    render={({ field }) => (
                      <SelectUser
                        isMulti={true}
                        id="select-collaborators-new-user"
                        role={UserRole.collaborator}
                        value={field.value || undefined}
                        onChange={(v) => field.onChange(v || [])}
                        placeholder="Seleziona collaboratori"
                        isClearable
                      />
                    )}
                  />
                </div>
              </div>

              {/* Company name */}
              <div className="flex gap-[24px] w-[100%] items-start">
                <div className="w-[240px] text-label-md font-medium text-neutral-600">Nome azienda <span className="text-red-600"> *</span></div>
                <div className="flex gap-[16px] w-[100%]">
                  <Controller
                    name="companyName"
                    control={control}
                    render={({ field }) => (
                      <Input {...field} value={field.value || ""} placeholder="Nome azienda" supportingText={errors.companyName?.message as string | undefined} error={!!errors.companyName} />
                    )}
                  />
                </div>
              </div>

              {/* VAT */}
              <div className="flex gap-[24px] w-[100%] items-start">
                <div className="w-[240px] text-label-md font-medium text-neutral-600">P. IVA azienda</div>
                <div className="flex gap-[16px] w-[100%]">
                  <Controller
                    name="companyVat"
                    control={control}
                    render={({ field }) => (
                      <Input {...field} value={field.value || ""} placeholder="P. IVA" supportingText={errors.companyVat?.message as string | undefined} error={!!errors.companyVat} />
                    )}
                  />
                </div>
              </div>

              {/* ATECO */}
              <div className="flex gap-[24px] w-[100%] items-start">
                <div className="w-[240px] text-label-md font-medium text-neutral-600">Codice ATECO</div>
                <div className="flex gap-[16px] w-[100%]">
                  <Controller
                    name="companyAtecoCode"
                    control={control}
                    render={({ field }) => (
                      <SelectCustom
                        placeholder="Cerca per ATECO"
                        isClearable
                        defaultValue={field.value ?? undefined}
                        onChange={(v) => field.onChange(toValue(v))}
                        options={atecos.map((ateco: any) => ({ value: ateco.atecoCode, label: `${ateco.atecoCode} - ${ateco.atecoDescription}` }))}
                      />
                    )}
                  />
                </div>
              </div>

              {/* Settore */}
              <div className="flex gap-[24px] w-[100%] items-start">
                <div className="w-[240px] text-label-md font-medium text-neutral-600">Settore</div>
                <div className="flex gap-[16px] w-[100%]">
                  <Input value={companySector} placeholder="Settore" disabled supportingText={errors.companySector?.message as string | undefined} error={!!errors.companySector} />
                </div>
              </div>

              {/* Revenue numeric */}
              <div className="flex gap-[24px] w-[100%] items-start">
                <div className="w-[240px] text-label-md font-medium text-neutral-600">Fatturato azienda</div>
                <div className="flex gap-[16px] w-[100%]">
                  <Controller
                    name="companyRevenue"
                    control={control}
                    render={({ field }) => (
                      <NumericFormat
                        prefix="€ "
                        value={field.value ?? ""}
                        thousandSeparator="."
                        decimalSeparator=","
                        decimalScale={2}
                        fixedDecimalScale
                        customInput={Input}
                        onValueChange={(vals) => field.onChange(vals.floatValue ?? undefined)}
                        placeholder="Fatturato"
                      />
                    )}
                  />
                </div>
              </div>

              {/* Website */}
              <div className="flex gap-[24px] w-[100%]">
                <div className="w-[240px] text-label-md font-medium text-neutral-600">Sito web</div>
                <div className="flex gap-[16px] w-[100%]">
                  <Controller
                    name="companyWebsite"
                    control={control}
                    render={({ field }) => (
                      <Input {...field} value={field.value || ""} placeholder="Sito web" supportingText={errors.companyWebsite?.message as string | undefined} error={!!errors.companyWebsite} />
                    )}
                  />
                </div>
              </div>
            </>
          )}

          {/* customer select */}
          {role === UserRole.collaborator && (
            <div className="flex gap-[24px] w-[100%] items-start">
              <div className="w-[240px] text-label-md font-medium text-neutral-600">Cliente</div>
              <div className="flex gap-[16px] w-[100%]">
                <Controller
                  name="customerId"
                  control={control}
                  render={({ field }) => (
                    <SelectUser
                      id="select-customer-new-user"
                      role={UserRole.customer}
                      value={field.value || null}
                      onChange={(v) => field.onChange(v || "")}
                      placeholder="Seleziona cliente"
                      isClearable
                    />
                  )}
                />
              </div>
            </div>
          )}

          {/* Avatar + uploader */}
          <div className="flex gap-[24px] w-[100%] items-start">
            <div className="w-[240px] text-label-md font-medium text-neutral-600">Immagine del profilo</div>
            <div className="flex gap-[16px] w-[100%]">
              <Avatar type="single" size={"2xl"} shape={"circle"} imageUrl={preview || ''} fullName={`${watch("name") || ""} ${watch("surname") || ""}`} altTextInitials={`${(watch("name")?.[0] ?? "")}${(watch("surname")?.[0] ?? "")}`} />
              <FileUploader id={"new-user-file-uploader"} style={"small"} onChange={(files: File[]) => setFile(files[0])} />
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
